import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FHSimpleTable } from '../../../base';
import { invoiceHistoryDataTransformer } from '../utils';
import { areUserRolesIn, history } from '../../../../helpers';
import { getInvoiceHistoryColumns, getHistoryTableOptions } from '../table-configs/';
import { Roles } from '../../../../constants';

import type { IClaimInvoice } from '../../../../utils/types';
import type { HistoricalDocuments } from '../../../../pages/constants';
import type { InvoiceStatus } from '../../../../constants';
import type { RootState } from '../../../../store';

interface IClaimInvoiceHistoryTableData {
  id: number;
  status: InvoiceStatus;
  created_by: string;
  billed_to: string;
  invoice_id: string;
  amount_billed: string; // Amount billed and amount due will be returned as a string
  amount_due: string; //  as it will be formatted as currency using usDollarFormatter
  date_sent: string;
}

interface IInvoiceHistoryTableProps {
  data: IClaimInvoice[];
  documentType: HistoricalDocuments;
}

function InvoiceHistoryTable({ data = [], documentType }: IInvoiceHistoryTableProps) {
  const { user } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation('invoice');

  const tableOptions = useMemo(() => {
    return {
      ...getHistoryTableOptions(documentType),
      onRowClick: (args: any) => {
        if (
          areUserRolesIn(user?.roles, [Roles.LimitedAccessGuardian, Roles.Guardian, Roles.Prbt])
        ) {
          history.navigate(`/billings/invoice-payment/${args[0]}`);
        } else {
          history.navigate(`/billings/invoice/${args[0]}`);
        }
      },
    };
  }, [documentType]);

  const [historyTableData, setHistoryTableData] = useState<IClaimInvoiceHistoryTableData[]>([]);

  useEffect(() => {
    const transformedTableData = invoiceHistoryDataTransformer(data);
    setHistoryTableData(transformedTableData);
  }, [data]);

  return (
    <Box data-testid="historicalInvoicesTable">
      <FHSimpleTable
        data={historyTableData}
        columnDefinitions={getInvoiceHistoryColumns(t)}
        columnOptions={tableOptions}
      />
    </Box>
  );
}

export default InvoiceHistoryTable;
